import api from './api';
import { getCurrentUser } from './modules/system/auth';
import { updateProfile } from './modules/system/settings';
import { unwrapResultData } from '../utils/axiosResult';
import { buildSafePageParams } from '../utils/pagination';
import type { Result } from './types';
import type { User } from '../types';

const ENABLE_USER_MOCK_IN_DEV =
  import.meta.env.DEV && String(import.meta.env.VITE_ENABLE_USER_MOCK || '').toLowerCase() === 'true';

function sanitizeUser(userData: User): User {
  const cloned = { ...userData } as Record<string, unknown>;
  delete cloned.password;
  return cloned as unknown as User;
}

function mapApiRoleToUserRole(roleValue: string): User['role'] {
  const raw = roleValue.toLowerCase();
  if (raw === 'super_admin' || raw.includes('super')) return 'super_admin';
  if (raw === 'admin' || raw.includes('administrator')) return 'admin';
  if (raw.includes('manager') || raw.includes('hr') || raw.includes('finance')) return 'manager';
  if (raw.includes('consult') || raw.includes('advisor') || raw.includes('研发') || raw.includes('技术')) return 'consultant';
  return 'sales';
}

export function mapApiUserToUser(row: Record<string, unknown>): User {
  const roleText =
    typeof row.role === 'string'
      ? row.role
      : typeof row.roleName === 'string'
        ? row.roleName
        : '';
  const hrEmployeeId =
    typeof row.hrEmployeeId === 'string' && row.hrEmployeeId.trim() !== ''
      ? row.hrEmployeeId.trim()
      : undefined;
  return sanitizeUser({
    id: String(row.id ?? ''),
    name: String(row.realName ?? row.name ?? row.username ?? ''),
    email: String(row.email ?? ''),
    avatar: String(row.avatar ?? ''),
    role: mapApiRoleToUserRole(roleText),
    department: String(row.deptName ?? row.department ?? ''),
    hrEmployeeId,
    position: String(row.position ?? ''),
  });
}

export async function fetchUsersFromApi(): Promise<User[]> {
  try {
    const raw = (await api.get('/users', { params: buildSafePageParams() })) as {
      code?: number;
      data?: { records?: Record<string, unknown>[] } | Record<string, unknown>[];
    };
    if (raw?.code !== 200 || !raw.data) {
      return [];
    }
    const rows = Array.isArray(raw.data) ? raw.data : raw.data.records ?? [];
    return rows.map(mapApiUserToUser);
  } catch (err) {
    // 路由切换/组件卸载时 axios 被 abort，interceptor 会 reject {__CANCELLED__: true}
    // 这是正常 cleanup，不是失败，安静跳过即可
    if (err && typeof err === 'object' && '__CANCELLED__' in err) {
      return [];
    }
    if (ENABLE_USER_MOCK_IN_DEV) {
      return [];
    }
    throw new Error('获取用户列表失败');
  }
}

export async function fetchCurrentUserFromApi(): Promise<User | null> {
  const raw = (await getCurrentUser()) as Result<User | Record<string, unknown>>;
  const data = unwrapResultData<User | Record<string, unknown>>(raw);
  if (!data) {
    return null;
  }
  if ('role' in (data as Record<string, unknown>) && 'id' in (data as Record<string, unknown>)) {
    return sanitizeUser(data as User);
  }
  return mapApiUserToUser(data as Record<string, unknown>);
}

export async function updateCurrentUserProfile(profile: {
  name: string;
  email: string;
  department: string;
}): Promise<User | null> {
  await updateProfile(profile as unknown as Record<string, unknown>);
  return fetchCurrentUserFromApi();
}
